import { useEffect, useState } from "react";
import { api, MODELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { GraduationCap, RefreshCw, ExternalLink, BookOpen, Download, FileText, Sparkles, Loader2, Search, Copy, BookMarked } from "lucide-react";

const TYPE_ICON = { ebook: BookOpen, guide: FileText, paper: FileText, course: GraduationCap, video: Sparkles };

export default function Learn() {
  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="mb-6">
        <div className="overline">Educate</div>
        <h1 className="font-display text-5xl mt-2 flex items-center gap-3"><GraduationCap className="w-10 h-10 text-primary"/>Learn grants &amp; publishing</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Find books, journals, papers, and guides — and let AI compose your citations, references, and index terms.</p>
      </div>
      <Tabs defaultValue="library">
        <TabsList>
          <TabsTrigger value="library" data-testid="learn-tab-lib">Library search</TabsTrigger>
          <TabsTrigger value="cite" data-testid="learn-tab-cite">Citations &amp; index</TabsTrigger>
        </TabsList>
        <TabsContent value="library"><LibrarySearch/></TabsContent>
        <TabsContent value="cite"><CitationGatherer/></TabsContent>
      </Tabs>
    </div>
  );
}

function LibrarySearch() {
  const [items, setItems] = useState([]); const [topic, setTopic] = useState(""); const [stream, setStream] = useState("");
  const [subject, setSubject] = useState(""); const [refreshing, setRefreshing] = useState(false); const [modelIdx, setModelIdx] = useState(0);

  const load = () => {
    const q = [topic, stream, subject].filter(Boolean).join(" ");
    api.get("/learn/resources", { params: q ? { topic: q } : {} }).then(r => setItems(r.data.items || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const refresh = async () => {
    setRefreshing(true);
    const query = [topic, stream, subject].filter(Boolean).join(" — ") || "Grant Writing & Publications";
    try {
      const m = MODELS[modelIdx];
      const { data } = await api.post("/learn/refresh", { topic: query, model_provider: m.provider, model_name: m.name });
      setItems(data.items || []); toast.success(`Refreshed — ${data.items?.length||0} resources`);
    } catch { toast.error("Refresh failed — showing curated list"); load(); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="mt-6">
      <div className="card-crisp rounded-lg p-5 mb-6 grid md:grid-cols-5 gap-3 items-end">
        <div><Label>Topic</Label><Input placeholder="e.g., NIH R01, ERC Advanced…" value={topic} onChange={(e)=>setTopic(e.target.value)} data-testid="learn-topic"/></div>
        <div><Label>Stream</Label><Input placeholder="e.g., STEM, Medicine, Humanities" value={stream} onChange={(e)=>setStream(e.target.value)} data-testid="learn-stream"/></div>
        <div><Label>Subject</Label><Input placeholder="e.g., Radiology, ML, Climate" value={subject} onChange={(e)=>setSubject(e.target.value)} data-testid="learn-subject"/></div>
        <div><Label>Model</Label>
          <Select value={String(modelIdx)} onValueChange={(v)=>setModelIdx(Number(v))}><SelectTrigger data-testid="learn-model"><SelectValue/></SelectTrigger><SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="rounded-full" data-testid="learn-search"><Search className="w-4 h-4 mr-1"/>Search</Button>
          <Button onClick={refresh} disabled={refreshing} className="rounded-full flex-1" data-testid="learn-refresh">{refreshing ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4 mr-1"/>}Fresh</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r) => {
          const Icon = TYPE_ICON[r.type] || FileText;
          return (
            <div key={r.id || r.url} className="card-crisp rounded-lg p-5 flex flex-col" data-testid={`learn-item-${(r.title||'').slice(0,15)}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center"><Icon className="w-5 h-5"/></div>
                <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-secondary text-muted-foreground">{r.type || "resource"}</span>
              </div>
              <div className="font-display text-xl mt-3 leading-tight line-clamp-2">{r.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{r.author}{r.year ? ` · ${r.year}` : ""}{r.source ? ` · ${r.source}` : ""}</div>
              <div className="text-sm text-muted-foreground mt-3 line-clamp-3 flex-1">{r.summary}</div>
              <div className="mt-4 flex gap-2">
                {r.url && <a href={r.url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-full"><ExternalLink className="w-3.5 h-3.5 mr-1"/>Open</Button></a>}
                <span className="text-xs text-muted-foreground self-center">{r.topic}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 text-right"><Button variant="outline" onClick={()=>window.print()}><Download className="w-4 h-4 mr-2"/>Print list</Button></div>
    </div>
  );
}

const STYLES = ["APA","MLA","IEEE","Chicago","Vancouver","Harvard"];

function CitationGatherer() {
  const [query, setQuery] = useState(""); const [style, setStyle] = useState("APA"); const [modelIdx, setModelIdx] = useState(0);
  const [busy, setBusy] = useState(false); const [res, setRes] = useState(null);

  const run = async () => {
    if (!query.trim()) return toast.error("Enter a topic, title, or DOI");
    setBusy(true); setRes(null);
    try {
      const m = MODELS[modelIdx];
      const { data } = await api.post("/citations/gather", { query, style, model_provider:m.provider, model_name:m.name });
      setRes(data);
    } catch { toast.error("Could not gather citations"); }
    finally { setBusy(false); }
  };

  const copyAll = async () => {
    if (!res) return;
    const text = (res.citations||[]).map((c,i)=>`${i+1}. ${c.formatted}`).join("\n");
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="mt-6">
      <div className="card-crisp rounded-lg p-5 mb-6 grid md:grid-cols-4 gap-3 items-end">
        <div className="md:col-span-2"><Label>Topic, title, or DOI</Label><Input value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="Explainable AI in radiology, or 10.1145/…" data-testid="cite-query"/></div>
        <div><Label>Style</Label><Select value={style} onValueChange={setStyle}><SelectTrigger data-testid="cite-style"><SelectValue/></SelectTrigger><SelectContent>{STYLES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Model</Label><Select value={String(modelIdx)} onValueChange={(v)=>setModelIdx(Number(v))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent></Select></div>
        <Button onClick={run} disabled={busy} className="rounded-full md:col-span-4" data-testid="cite-run">{busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Gathering…</> : <><Sparkles className="w-4 h-4 mr-2"/>Gather citations, keywords &amp; databases</>}</Button>
      </div>

      {res && (
        <div className="space-y-5" data-testid="cite-result">
          {res.keywords?.length > 0 && (
            <div className="card-crisp rounded-lg p-5">
              <div className="font-display text-2xl mb-2">Index keywords</div>
              <div className="flex flex-wrap gap-2">{res.keywords.map((k,i)=>(<span key={i} className="px-3 py-1 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">{k}</span>))}</div>
            </div>
          )}
          {res.suggested_databases?.length > 0 && (
            <div className="card-crisp rounded-lg p-5">
              <div className="font-display text-2xl mb-2">Search these databases</div>
              <div className="flex flex-wrap gap-2">{res.suggested_databases.map((k,i)=>(<span key={i} className="px-3 py-1 text-xs rounded-full bg-secondary">{k}</span>))}</div>
            </div>
          )}
          <div className="card-crisp rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-2xl flex items-center gap-2"><BookMarked className="w-5 h-5 text-primary"/>References ({style})</div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copyAll} data-testid="cite-copy"><Copy className="w-4 h-4 mr-1"/>Copy all</Button>
                <Button variant="outline" size="sm" onClick={()=>window.print()}><Download className="w-4 h-4 mr-1"/>Print</Button>
              </div>
            </div>
            <ol className="list-decimal pl-6 space-y-2 text-sm">
              {(res.citations||[]).map((c,i)=>(
                <li key={i}>
                  <div>{c.formatted}</div>
                  {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="text-primary text-xs hover:underline inline-flex items-center gap-1 mt-1">{c.url} <ExternalLink className="w-3 h-3"/></a>}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
